import { ChangeEvent, FocusEvent, KeyboardEvent, InputHTMLAttributes } from 'react';
import { InputAdornment, SxProps, TextField, type TextFieldProps, Theme } from '@mui/material';
import { normaliseUnicodeText } from '../../../Utils/normaliseUnicodeText';

export interface TextInputProps {
  value?: string | number;
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
  error?: string;
  label: string;
  id: string;
  isDisabled?: boolean;
  type?: 'number' | 'text' | 'password' | 'email' | 'url';
  endAdornment?: React.ReactNode;
  autoComplete?: string;
  required?: boolean;
  placeholder?: string;
  sx?: SxProps<Theme>;
  multiline?: boolean;
  rows?: number;
  inputProps?: InputHTMLAttributes<HTMLInputElement>;
  onFocus?: (event: FocusEvent<HTMLInputElement>) => void;
  onBlur?: (event: FocusEvent<HTMLInputElement>) => void;
  onKeyDown?: (event: KeyboardEvent<HTMLInputElement>) => void;
  ariaLabel?: string;
  disableNormalise?: boolean;
  variant?: TextFieldProps['variant'];
  surface?: 'default' | 'inverse';
}

/**
 *
 * Base TextInput component, change the type for different input types
 */

export const TextInput = ({
  value,
  onChange,
  error,
  label,
  id,
  isDisabled = false,
  type = 'text',
  endAdornment,
  autoComplete,
  placeholder,
  sx,
  rows,
  multiline = false,
  required = false,
  inputProps,
  onFocus,
  onBlur,
  onKeyDown,
  ariaLabel,
  disableNormalise = false,
  variant = 'outlined',
  surface = 'default',
}: TextInputProps) => {
  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (!disableNormalise) {
      const normalised = normaliseUnicodeText(event.target.value);
      if (normalised !== event.target.value) {
        event.target.value = normalised;
      }
    }
    onChange(event);
  };

  const inverseSurfaceSx: SxProps<Theme> = {
    '& .MuiInputLabel-root': {
      color: 'darkGray.main',
      '&.Mui-focused, &.MuiInputLabel-shrink': {
        color: 'darkGray.main',
      },
      '&.Mui-error': {
        color: 'error.main',
      },
    },
    '& .MuiFilledInput-root': {
      backgroundColor: 'common.white',
      '&:hover, &.Mui-focused': {
        backgroundColor: 'common.white',
      },
      '&::before, &::after': {
        display: 'none',
      },
    },
    '& .MuiInputBase-input': {
      color: 'common.black',
    },
    '& .MuiFormHelperText-root': {
      color: 'error.main',
    },
  };

  const resolvedSx = surface === 'inverse' ? ([inverseSurfaceSx, sx] as SxProps<Theme>) : sx;

  return (
    <TextField
      fullWidth
      id={id}
      label={required ? `${label}*` : label}
      variant={variant}
      value={value}
      error={Boolean(error)}
      helperText={error}
      onChange={handleChange}
      disabled={isDisabled}
      placeholder={placeholder}
      type={type}
      sx={resolvedSx}
      multiline={multiline}
      rows={rows}
      onFocus={onFocus}
      onBlur={onBlur}
      onKeyDown={onKeyDown}
      slotProps={{
        input: {
          endAdornment: endAdornment && (
            <InputAdornment position="end">{endAdornment}</InputAdornment>
          ),
          inputProps: {
            'aria-label': ariaLabel,
          },
        },
        htmlInput: inputProps,
      }}
      autoComplete={autoComplete}
    />
  );
};
